import React, { useState, useEffect, useMemo } from "react";
import {
  Textarea,
  Button,
  Group,
  Select,
  Flex,
  Divider,
  Text,
  Drawer,
  Stack,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import "dayjs/locale/uk";
import "dayjs/locale/en";

import useDevelopmentStore from "../../../store/useDevelopmentStore";
import useAuthStore from "../../../store/useAuthStore";
import useDashboardStore from "../../../store/useDashboardStore";
import { useTranslation } from "react-i18next";
import { Goal } from "../../../types/api";
import useProfileStore from "../../../store/useProfileStore";
import ModalWithSearchBar from "../../search-bar/GoalSearchBar";
import { InfoTooltip } from "../../../shared/components/InfoTooltip";
import { TrashCan } from "../../../shared/svg/TrashCan";
import ConfirmDelete from "../../../utils/components/ConfirmDelete";
import { GoalTasks } from "./GoalTasks";
import { DateInput, DatesProvider } from "@mantine/dates";
import { IconCalendar } from "../../../shared/svg/IconCalendar";

type ResourceLink = { learning_course_id: number };

interface GoalFormValues {
  title: string;
  description: string;
  status: "planned" | "in_progress" | "completed";
  priority: "low" | "medium" | "high";
  deadline: string;
  resource_links?: ResourceLink[] | [];
}

interface GoalModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  goal: Goal | null; // Отримуємо ціль для редагування
}

const statusMapping: Record<"planned" | "in_progress" | "completed", string> = {
  planned: "Planned",
  in_progress: "In Progress",
  completed: "Completed",
};

const priorityMapping: Record<"low" | "medium" | "high", string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const EditGoalModal = ({ isOpen, setIsOpen, goal }: GoalModalProps) => {
  const [formValues, setFormValues] = useState<GoalFormValues>({
    title: "",
    description: "",
    status: "planned",
    priority: "high",
    deadline: "",
    resource_links: [],
  });
  const matches = useMediaQuery("(max-width: 650px)");
  const { profileLanguage } = useProfileStore();
  useEffect(() => {
    if (goal) {
      setFormValues({
        title: goal.title,
        description: goal.description,
        status: goal.status,
        priority: goal.priority,
        deadline: goal.deadline,
        resource_links:
          goal.resource_links
            ?.filter((course) => course.id !== undefined)
            .map((course) => ({
              learning_course_id: course.id as number,
            })) || [],
      });
    }
  }, [goal, isOpen]);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { editGoal, currentPlanId, removeGoal, goals } = useDevelopmentStore();
  const { fetchDashboard } = useDashboardStore();
  const { access_token } = useAuthStore();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const currentGoal = goals.find((g) => g.id === goal?.id);

  const deadlineDate = useMemo(() => {
    return formValues.deadline ? new Date(formValues.deadline) : null;
  }, [formValues.deadline]);

  useEffect(() => {
    dayjs.locale(Object.keys(profileLanguage!)[0]);
  }, [profileLanguage]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleStatusChange = (value: string | null) => {
    if (value && ["planned", "in_progress", "completed"].includes(value)) {
      setFormValues((prev) => ({
        ...prev,
        status: value as "planned" | "in_progress" | "completed",
      }));
    }
  };

  const handlePriorityChange = (value: string | null) => {
    if (value && ["low", "medium", "high"].includes(value)) {
      setFormValues((prev) => ({
        ...prev,
        priority: value as "low" | "medium" | "high",
      }));
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let validationErrors: { [key: string]: string } = {};
    if (!formValues.title) {
      validationErrors.title = t("form.titleRequired");
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      await editGoal({
        planId: currentPlanId!,
        credentials: { ...formValues, id: goal!.id },
      });
      fetchDashboard(access_token!);
      setIsOpen(false);
      resetForm();
    } catch (err: any) {
      setErrors(
        Object.keys(err.response.data.errors).reduce((acc, key) => {
          acc[key] = err.response.data.errors[key][0];
          return acc;
        }, {} as { [key: string]: string })
      );
    }
    setIsLoading(false);
  };

  const resetForm = () => {
    setFormValues({
      title: "",
      description: "",
      deadline: "",
      status: "planned",
      priority: "high",
      resource_links: [],
    });
    setErrors({});
  };

  const handleResourceLink = (links: ResourceLink[]) => {
    setFormValues((prev) => ({
      ...prev,
      resource_links: links,
    }));
  };
  const handleDateChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      deadline: value,
    }));
  };

  return (
    <>
      <Drawer
        zIndex={301}
        opened={isOpen}
        onClose={() => {
          resetForm();
          setIsOpen(false);
        }}
        title={<Text fw={600}>{t("form.editGoal")}</Text>}
        size="lg"
        padding={30}
        transitionProps={{ duration: 300 }}
      >
        <Textarea
          label={t("form.title")}
          placeholder={t("form.enterGoalTitle")}
          value={formValues.title}
          onChange={(e) => handleChange(e, "title")}
          required
          error={errors.title}
          autosize
          minRows={1}
          rightSection={
            <div
              style={{ display: "flex", alignItems: "center", height: "100%" }}
            >
              <InfoTooltip label={t("goal_title_description")} size={25} />
            </div>
          }
        />

        <Textarea
          label={t("form.description")}
          placeholder={t("form.enterGoalDescription")}
          value={formValues.description}
          onChange={(e) => handleChange(e, "description")}
          mt="md"
          error={errors.description}
          autosize
          minRows={1}
          rightSection={
            <div
              style={{ display: "flex", alignItems: "center", height: "100%" }}
            >
              <InfoTooltip label={t("goal_notes_description")} size={25} />
            </div>
          }
        />
        <DatesProvider settings={{ locale: Object.keys(profileLanguage!)[0] }}>
          <DateInput
            mt={32}
            label={t("deadline")}
            placeholder={t("choose_date")}
            value={deadlineDate}
            onChange={(date) =>
              handleDateChange(date ? dayjs(date).format("YYYY-MM-DD") : "")
            }
            clearable
            popoverProps={{
              zIndex: 302,
            }}
            leftSection={<IconCalendar />}
            renderDay={(date) => {
              const isToday = dayjs(date).isSame(new Date(), "day");

              return (
                <Box
                  style={{
                    backgroundColor: isToday ? "#faf0fd" : "transparent",
                    color: isToday ? "#862e9c" : "inherit",
                    border: isToday ? "1px solid #862e9c" : "none",
                    borderRadius: 6,
                    width: 34,
                    height: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: isToday ? 700 : 400,
                  }}
                >
                  {date.getDate()}
                </Box>
              );
            }}
          />
        </DatesProvider>

        <Flex direction={matches ? "column" : "row"} gap="md">
          <Select
            label={t("form.status")}
            value={formValues.status}
            onChange={handleStatusChange}
            data={Object.keys(statusMapping).map((key) => ({
              value: key,
              label: t(`status.${key}`),
            }))}
            mt="md"
            w={matches ? "100%" : "50%"}
            comboboxProps={{
              zIndex: 302,
              withinPortal: true,
            }}
          />

          <Select
            label={t("form.priority")}
            value={formValues.priority}
            onChange={handlePriorityChange}
            data={Object.keys(priorityMapping).map((key) => ({
              value: key,
              label: t(`priority.${key}`),
            }))}
            mt="md"
            w={matches ? "100%" : "50%"}
            comboboxProps={{
              zIndex: 302,
              withinPortal: true,
            }}
          />
        </Flex>

        <Divider
          label={t("calculated_automatically")}
          labelPosition="center"
          my="lg"
        />
        <Stack gap={4}>
          <Group justify="space-around">
            <Flex direction="column" align="center">
              <Text size="xs" c="dimmed">
                {t("progress")}
              </Text>
              <Text>{currentGoal?.progress ?? 0}%</Text>
            </Flex>
            <Flex direction="column" align="center">
              <Text size="xs" c="dimmed">
                {t("total_tasks")}
              </Text>
              <Text>{currentGoal?.total_tasks ?? 0}</Text>
            </Flex>
            <Flex direction="column" align="center">
              <Text size="xs" c="dimmed">
                {t("completed_tasks")}
              </Text>
              <Text>{currentGoal?.completed_tasks ?? 0}</Text>
            </Flex>
          </Group>
        </Stack>

        <Divider my="lg" color="gray.4" />
        <ModalWithSearchBar
          handleResourceLink={handleResourceLink}
          relatedCourses={
            goal?.resource_links
              ? goal?.resource_links.map((item) => item.learning_course)
              : undefined
          }
        />
        <Divider my="lg" color="gray.4" />

        <GoalTasks goalId={goal?.id ? goal?.id : null} />

        <Divider my="lg" color="gray.4" />

        <Group
          mt={40}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button
            size={matches ? "compact-md" : "md"}
            variant="light"
            onClick={() => setIsDeleteOpen(true)}
          >
            <TrashCan />
          </Button>
          <Flex align="center" gap={10}>
            <Button
              size={matches ? "compact-md" : "md"}
              onClick={() => {
                resetForm();
                setIsOpen(false);
              }}
              variant="outline"
            >
              {t("cancel")}
            </Button>

            <Button
              onClick={handleSubmit}
              size={matches ? "compact-md" : "md"}
              loading={isLoading}
            >
              {t("form.submit")}
            </Button>
          </Flex>
        </Group>
        <ConfirmDelete
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
          }}
          onDelete={() => {
            if (goal) {
              removeGoal(currentPlanId!, goal.id);
              fetchDashboard(access_token!);
              setIsDeleteOpen(false);
              setIsOpen(false);
            }
          }}
          title={t("delete_goal_title")}
        />
      </Drawer>
    </>
  );
};

export default EditGoalModal;
